import React, { useState } from 'react';
import { History, Clock, Trash2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения истории генераций случайных чисел
 */
const GeneratorHistory = ({ history, setHistory, loadFromHistory }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!history || history.length === 0) return null;

  // Очистка всей истории
  const clearHistory = () => {
    if (window.confirm('Вы уверены, что хотите очистить всю историю генераций?')) {
      setHistory([]);
      localStorage.removeItem('generatorHistory');
    }
  };

  // Загрузка конфигурации и результатов из истории
  const handleLoadFromHistory = (entry) => {
    loadFromHistory(entry.config, entry.results);
  };

  // Удаление одной записи из истории
  const removeHistoryEntry = (id, e) => {
    e.stopPropagation(); // Предотвращаем всплытие события клика
    const updatedHistory = history.filter(entry => entry.id !== id);
    setHistory(updatedHistory);
    localStorage.setItem('generatorHistory', JSON.stringify(updatedHistory));
  };

  // Форматирование даты в читаемом виде
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Получение краткого описания для записи истории
  const getHistoryEntryDescription = (entry) => {
    return `${entry.config.min}-${entry.config.max}, ${entry.results.length} чисел`;
  };

  // Получение первых N чисел из результатов
  const getPreviewNumbers = (numbers, count = 5) => {
    if (!numbers || numbers.length === 0) return "Нет результатов";
    
    const preview = numbers.slice(0, count).join(', ');
    return numbers.length > count 
      ? `${preview}, ... (ещё ${numbers.length - count})` 
      : preview;
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-6 ${isExpanded ? 'transition-all duration-300 ease-in-out' : ''}`}>
      {/* Заголовок с кнопкой раскрытия/сворачивания */}
      <div 
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <History className="w-5 h-5 mr-2 text-indigo-500" />
          История генераций
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
            ({history.length})
          </span>
        </h3>
        
        <button 
          type="button"
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          aria-label={isExpanded ? "Свернуть историю" : "Развернуть историю"}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {/* Содержимое истории (видимое только при развёрнутом состоянии) */}
      {isExpanded && (
        <div className="mt-4 space-y-3">
          {history.map((entry) => (
            <div 
              key={entry.id}
              className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              onClick={() => handleLoadFromHistory(entry)}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1.5" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(entry.date)}
                  </span>
                </div>
                
                <button
                  type="button"
                  onClick={(e) => removeHistoryEntry(entry.id, e)}
                  className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  aria-label="Удалить из истории"
                  title="Удалить из истории"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              
              <div className="font-medium text-gray-900 dark:text-white">
                {getHistoryEntryDescription(entry)}
              </div>
              
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 truncate">
                {getPreviewNumbers(entry.results)}
              </div>
              
              <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                {entry.config.unique && (
                  <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                    Уникальные
                  </span>
                )}
                
                {entry.config.sorting !== 'none' && (
                  <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                    {entry.config.sorting === 'asc' ? 'По возрастанию' : 'По убыванию'}
                  </span>
                )}
                
                {entry.config.distribution !== 'uniform' && (
                  <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                    {entry.config.distribution === 'normal' ? 'Нормальное' : 'Взвешенное'}
                  </span>
                )}
              </div>
            </div>
          ))}
          
          {/* Кнопка очистки всей истории */}
          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearHistory}
              icon={<Trash2 className="w-4 h-4" />}
            >
              Очистить историю
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratorHistory;